import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useInsertGlobalActionMutation,
  useLockGlobalActionMutation,
  useSetGlobalActionEnabledMutation,
  useUnlockGlobalActionMutation,
  useUpdateGlobalActionMutation,
} from 'services/globalActions/globalActions.api';
import { usePerms } from 'contexts/PermsContext';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import ActionBuilderHeader from './components/ActionBuilderHeader';
import GlobalActionWarningModal from './components/GlobalActionWarningModal';
import BuilderSection from './features/BuilderSection/index.web';
import {
  buildGlobalActionPayload,
  createBuilderItem,
  validateBuilderState,
} from './features/BuilderSection/utils/builderForm.utils';
import useActionBuilderState from './hooks/useActionBuilderState';
import useLockRefresh from './hooks/useLockRefresh';
import {
  GLOBAL_ACTION_FINDING_LABELS,
  readGlobalActionSaveError,
} from './utils/globalActionSaveError.utils';
import IpawsBackendFindingsList from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/components/IpawsBackendFindingsList/IpawsBackendFindingsList';
import { useGetFemaMetaQuery } from 'services/ipaws/ipaws.api';
import {
  describeIpawsFemaClass,
  getIpawsActualSaveNotice,
  resolveIpawsFemaClass,
} from './utils/ipawsSendMode.utils';
import {
  IPAWS_IN_FLIGHT_NOTICE,
  isIpawsActionInFlight,
  isIpawsInFlightRefusal,
} from './utils/ipawsInFlight.utils';
import {
  actionPermissionNotice,
  missingActionPermissions,
  readGlobalActionPermissionRefusal,
} from './utils/actionPermissions.utils';
import '../../GlobalActions.scss';

const ALARM_SHARE_LIST_HIDDEN_MARKER = '__alarm_share_hidden__';

const ActionBuilder = ({
  initialData,
  setInitialData,
  setShowGlobalAction,
  readOnly,
  onAlarmShareSuccess,
  onAlarmShareCancel,
}) => {
  const perms = usePerms();
  const isAlarmSharePrefill = Boolean(initialData?.alarmSharePrefill);
  const [hasStartedActions, setHasStartedActions] = useState(Boolean(initialData?.actions?.length));
  const [hasStartedUntil, setHasStartedUntil] = useState(Boolean(initialData?.resolutions?.length));
  const [submitError, setSubmitError] = useState('');
  const [submitFindings, setSubmitFindings] = useState([]);
  const previousBuilderState = useRef(null);
  const [activeEditWarning, setActiveEditWarning] = useState(false);
  const [insertGlobalAction, { isLoading: isCreating }] = useInsertGlobalActionMutation();
  const [setGlobalActionEnabled] = useSetGlobalActionEnabledMutation();
  const [updateGlobalAction, { isLoading: isUpdating }] = useUpdateGlobalActionMutation();
  const [unlockGlobalAction] = useUnlockGlobalActionMutation();
  const [lockGlobalAction] = useLockGlobalActionMutation();
  const {
    builderState,
    updateMetadata, // Used to update the metadata (action name, debounce time, etc.) of the global action in the builder state
    addConditionGroup, // Used to add a new condition group (OR) for the IF conditions section in the builder state
    addCondition, // Used to add a new condition (IF) to the builder state
    updateCondition, // Used to update a condition (IF) in the builder state
    removeCondition, // Used to remove a condition (IF) from the builder state
    addAction, // Used to add a new action (THEN) to the builder state
    updateAction, // Used to update an action (THEN) in the builder state
    removeAction, // Used to remove an action (THEN) from the builder state
    addUntilGroup, // Used to add a new until group (OR) for the UNTIL conditions section in the builder state
    removeUntilGroup, // Used to remove an until group (OR) from the UNTIL conditions section in the builder state
    addUntil, // Used to add a new until condition to the builder state
    updateUntil, // Used to update an until condition in the builder state
    removeUntil, // Used to remove an until condition from the builder state
  } = useActionBuilderState(initialData);

  useEffect(() => {
    setHasStartedActions(Boolean(initialData?.actions?.length));
    setHasStartedUntil(Boolean(initialData?.resolutions?.length));
  }, [initialData]);

  // Clear the save error when the operator changes the builder. With `submitError` in the
  // dependencies, the error cleared itself on the next render, so a 400 body never stayed.
  useEffect(() => {
    if (previousBuilderState.current && previousBuilderState.current !== builderState) {
      setSubmitError('');
      setSubmitFindings([]);
    }
    previousBuilderState.current = builderState;
  }, [builderState]);

  const validation = useMemo(() => validateBuilderState(builderState), [builderState]);
  const hasSentIpawsMessages = builderState.actions.some(
    (action) =>
      action.category === 'Publish IPAWS Alert' &&
      Array.isArray(action.formData?.sent) &&
      action.formData.sent.length > 0,
  );
  // The save confirmation names the class for an Actual IPAWS action (Mike, 2026-09-26). The
  // environment select offers only the served class today, so the served class is the label.
  const femaMetaQuery = useGetFemaMetaQuery();
  const ipawsActualNotice = getIpawsActualSaveNotice(
    builderState.actions,
    describeIpawsFemaClass(resolveIpawsFemaClass(null, femaMetaQuery.data), femaMetaQuery.data),
  );
  const isSubmitting = isCreating || isUpdating;
  const isEditMode = !readOnly && !isAlarmSharePrefill && Boolean(builderState.id);
  // An enabled IPAWS action with a message on the air is read-only (Mike, 2026-09-26). The operator
  // disables it in the list, edits it, and enables it again. The edit lock keeps the `readOnly` prop.
  const isInFlight = isIpawsActionInFlight(initialData);
  const editorReadOnly = readOnly || isInFlight;

  const { clearLockRefreshTimer } = useLockRefresh({
    id: builderState.id,
    isEditMode,
    lockGlobalAction,
    unlockGlobalAction,
  });

  //This useCallback is used to build the jumplinks in the validation error alert.
  // It checks the error message and returns the corresponding section ID for the link target.
  const getErrorTarget = useCallback(
    (message) => {
      if (message === 'A TITLE is required.') return 'action-builder-header';

      if (message === 'Add at least one IF trigger.' || message === 'Complete the IF trigger.')
        return 'action-builder-conditions';

      if (message === 'Add at least one THEN action.' || message === 'Complete the THEN action.')
        return 'action-builder-actions';

      if (
        message === 'Add at least one UNTIL resolution.' ||
        message === 'Complete the UNTIL resolution.' ||
        message.includes('UNTIL')
      )
        return 'action-builder-until';

      const triggerIds = new Set(builderState.conditions.flat().map((trigger) => trigger.id));
      const actionIds = new Set(builderState.actions.map((action) => action.id));
      const resolutionIds = new Set(builderState.until.flat().map((resolution) => resolution.id));

      for (const id of Object.keys(validation.rowErrors)) {
        if (triggerIds.has(id)) return 'action-builder-conditions';
        if (actionIds.has(id)) return 'action-builder-actions';
        if (resolutionIds.has(id)) return 'action-builder-until';
      }

      return null;
    },
    [builderState, validation.rowErrors],
  );

  const getAlarmShareExecutionTime = () => {
    const executionTime = new Date();
    executionTime.setSeconds(0, 0);
    executionTime.setMinutes(executionTime.getMinutes() + 1);
    return executionTime;
  };

  const extractGlobalActionId = (payload) => {
    if (!payload || typeof payload !== 'object') return null;

    if (Array.isArray(payload)) {
      for (const item of payload) {
        const nestedId = extractGlobalActionId(item);
        if (nestedId != null) return nestedId;
      }
      return null;
    }

    const candidateKeys = ['id', 'globalActionId', 'global_action_id', 'insertId'];
    for (const key of candidateKeys) {
      const value = payload[key];
      const asNumber = Number(value);
      if (Number.isFinite(asNumber) && asNumber > 0) {
        return asNumber;
      }
    }

    const nestedKeys = ['data', 'result'];
    for (const key of nestedKeys) {
      const nestedId = extractGlobalActionId(payload[key]);
      if (nestedId != null) return nestedId;
    }

    return null;
  };

  // Used to display the actions (THEN) section when the user clicks the THEN button
  const handleStartThen = () => {
    setHasStartedActions(true);

    if (builderState.actions.length === 0) {
      addAction(createBuilderItem('actions'));
    }
  };

  const handleStartUntil = () => {
    setHasStartedUntil(true);

    if (builderState.until.length === 0) {
      addUntilGroup();
      return;
    }

    if (builderState.until.every((group) => group.length === 0)) {
      addUntil(createBuilderItem('until'), builderState.until.length - 1);
    }
  };

  const buildAlarmShareSmsFormData = () => {
    const currentEmailAction = builderState.actions.find(
      (action) => action?.category === 'Send Email',
    )?.formData;
    const initialEmailAction = initialData?.actions?.find(
      (action) => action?.function === 'sendEmail',
    )?.params;
    const sourceFormData =
      (currentEmailAction && Object.keys(currentEmailAction).length > 0 && currentEmailAction) ||
      initialEmailAction ||
      {};

    return {
      subject: sourceFormData.subject || '',
      body: sourceFormData.body || '',
      recipient: '',
      recipients: [{ recipient: '', carrier: '' }],
      error: true,
    };
  };

  const handleAddActionRow = () => {
    if (isAlarmSharePrefill) {
      addAction({
        ...createBuilderItem('actions', 'Send SMS'),
        formData: buildAlarmShareSmsFormData(),
      });
      return;
    }

    addAction(createBuilderItem('actions'));
  };

  const handleRemoveAction = (id) => {
    // Removes the entire actions section if it is the last action
    if (builderState.actions.length <= 1) {
      removeAction(id);
      setHasStartedActions(false);
      return;
    }

    removeAction(id); // Removes the action from the builder state using the action's ID
  };

  const handleRemoveUntil = (id, groupIndex) => {
    const group = builderState.until[groupIndex] ?? []; // Gets the until group from the builder state with the until condition to be removed
    const totalUntilRows = builderState.until.reduce(
      (count, currentGroup) => count + currentGroup.length,
      0,
    );

    // Removes the entire until section if it is the last until condition
    if (totalUntilRows <= 1) {
      removeUntil(id);
      setHasStartedUntil(false);
      return;
    }

    // Removes the until group with all of its conditions if it is the last condition in the group
    if (group.length <= 1) {
      removeUntilGroup(groupIndex);
      return;
    }

    removeUntil(id); // Removes the until condition from the builder state using the until condition's ID
  };

  // Only call this after user confirms in modal
  const handleSubmit = async () => {
    try {
      setSubmitError('');

      const payload = await buildGlobalActionPayload(builderState);

      if (isAlarmSharePrefill && Array.isArray(payload?.triggers)) {
        const executionTime = getAlarmShareExecutionTime();
        payload.triggers = payload.triggers.map((group) =>
          Array.isArray(group)
            ? group.map((condition) =>
                condition?.type === 'datetime'
                  ? {
                      ...condition,
                      value: {
                        ...(condition.value || {}),
                        value: executionTime,
                      },
                    }
                  : condition,
              )
            : group,
        );
      }

      if (isAlarmSharePrefill) {
        payload.state = 'active';
        payload.status = true;
        payload.triggerName = ALARM_SHARE_LIST_HIDDEN_MARKER;
        payload.resolutionName = ALARM_SHARE_LIST_HIDDEN_MARKER;

        try {
          const rawDataList = JSON.parse(payload.rawDataList || '[]');
          if (Array.isArray(rawDataList) && rawDataList[0] && typeof rawDataList[0] === 'object') {
            rawDataList[0].hiddenFromGlobalActionsList = true;
            rawDataList[0].alarmSharePrefill = true;
            payload.rawDataList = JSON.stringify(rawDataList);
          }
        } catch (error) {
          // Keep payload save-path resilient if rawDataList format changes.
        }
      }

      let savedGlobalActionId = builderState.id || null;

      if (builderState.id) {
        const updateRequest = { id: builderState.id, action: payload };
        await updateGlobalAction(updateRequest).unwrap();
        clearLockRefreshTimer();
        void unlockGlobalAction(builderState.id);
      } else {
        const createdResponse = await insertGlobalAction(payload).unwrap();
        savedGlobalActionId = extractGlobalActionId(createdResponse);

        if (savedGlobalActionId == null && isAlarmSharePrefill) {
          throw new Error('Global action was saved, but could not be activated automatically.');
        }
      }

      if (isAlarmSharePrefill) {
        const enableRequest = { id: savedGlobalActionId, status: true };
        await setGlobalActionEnabled(enableRequest).unwrap();
      }

      notifySuccessToast('Global action', { isAddOperation: !builderState.id });

      if (isAlarmSharePrefill && typeof onAlarmShareSuccess === 'function') {
        onAlarmShareSuccess();
        return;
      }

      setInitialData(null);
      setShowGlobalAction(false);
    } catch (error) {
      console.error(
        builderState.id
          ? 'Failed to update global action with id'
          : 'Failed to create global action',
        builderState.id || '',
        error,
      );
      // The action triggered while the form was open: the backend refuses the edit with 409. The
      // form keeps the operator's changes.
      if (isIpawsInFlightRefusal(error)) {
        setSubmitError(IPAWS_IN_FLIGHT_NOTICE);
        setSubmitFindings([]);
        toast.error(IPAWS_IN_FLIGHT_NOTICE, { toastId: 'global-action-in-flight' });
        return;
      }
      // The backend permission middleware refuses with 403 GLOBAL_ACTION_PERMISSION_REQUIRED and
      // names the missing keys (for example, a permission removed while the form was open).
      const refusedMissing = readGlobalActionPermissionRefusal(error);
      if (refusedMissing) {
        const notice = actionPermissionNotice(refusedMissing);
        setSubmitError(notice);
        setSubmitFindings([]);
        toast.error(notice, { toastId: 'global-action-permission' });
        return;
      }
      if (error?.status === 423) {
        const lockedMessage =
          error?.raw?.message ||
          error?.message ||
          'This global action is locked by another user. Your changes could not be saved.';
        setSubmitError(lockedMessage);
        toast.error(lockedMessage, { toastId: 'global-action-locked' });
        return;
      }
      notifyErrorToast('Global action', { isAddOperation: !builderState.id });
      // A save-time IPAWS refusal answers 400 with findings (card BE-25). They show as the
      // Alert Creator result modal shows them: the input, the FEMA code and the CAP field.
      const { message, findings } = readGlobalActionSaveError(error);
      setSubmitError(message);
      setSubmitFindings(findings);
    }
  };

  // Validate, then show modal if required, then call handleSubmit on confirm
  const handleVerify = () => {
    // The backend refuses an action that the user may not use. Stop before the request.
    const missing = missingActionPermissions(perms, builderState.actions);
    if (missing.length > 0) {
      setSubmitError(actionPermissionNotice(missing));
      setSubmitFindings([]);
      return;
    }
    const nextValidation = validateBuilderState(builderState);
    if (!nextValidation.isValid) {
      setSubmitError(nextValidation.formError || 'Fix the highlighted builder rows before saving.');
      return;
    }
    if (initialData?.state === 'active' || hasSentIpawsMessages || ipawsActualNotice) {
      setActiveEditWarning(true);
    } else {
      handleSubmit();
    }
  };

  // Modal confirm/cancel handler
  const handleWarningConfirm = (confirmed) => {
    setActiveEditWarning(false);
    if (confirmed) {
      handleSubmit();
    }
  };

  const handleCancel = () => {
    if (isAlarmSharePrefill && typeof onAlarmShareCancel === 'function') {
      onAlarmShareCancel();
      return;
    }

    if (isEditMode) {
      clearLockRefreshTimer();
      void unlockGlobalAction(builderState.id);
    }

    setShowGlobalAction(false);
    setInitialData(null);
  };

  return (
    <div className="global-action-builder page-scroll-area px-2 d-flex flex-column gap-4">
      <GlobalActionWarningModal
        toWarn={activeEditWarning}
        globalAction={initialData}
        resolveWarning={handleWarningConfirm}
        mode={
          initialData?.state === 'active' ? 'edit' : hasSentIpawsMessages ? 'ipaws' : 'ipawsActual'
        }
        hasSentIpawsMessages={hasSentIpawsMessages}
        ipawsActualNotice={ipawsActualNotice}
      />

      {isInFlight && (
        <Alert variant="warning" role="status" className="mb-0 flex-shrink-0">
          {IPAWS_IN_FLIGHT_NOTICE}
        </Alert>
      )}

      {!isAlarmSharePrefill && (
        <>
          <div id="action-builder-header" className="overflow-hidden" style={{ height: 0 }} />
          <ActionBuilderHeader
            metadata={builderState.metadata}
            updateMetadata={updateMetadata}
            readOnly={editorReadOnly}
            setInitialData={setInitialData}
            setShowGlobalAction={setShowGlobalAction}
          />
        </>
      )}

      {!isAlarmSharePrefill && (
        <>
          <div id="action-builder-conditions" className="overflow-hidden" style={{ height: 0 }} />
          <BuilderSection
            section="conditions"
            items={builderState.conditions}
            readOnly={editorReadOnly}
            rowErrors={validation.rowErrors}
            onUpdateItem={updateCondition}
            onRemoveItem={removeCondition}
            onAddRow={() =>
              addCondition(
                createBuilderItem('conditions'),
                Math.max(builderState.conditions.length - 1, 0),
              )
            }
            onAddGroup={() => addConditionGroup()}
            onStartThen={handleStartThen}
          />
        </>
      )}

      {(hasStartedActions || builderState.actions.length > 0) && (
        <>
          <div id="action-builder-actions" className="overflow-hidden" style={{ height: 0 }} />
          <BuilderSection
            section="actions"
            items={builderState.actions}
            readOnly={editorReadOnly}
            rowErrors={validation.rowErrors}
            onUpdateItem={updateAction}
            onRemoveItem={handleRemoveAction}
            onAddRow={handleAddActionRow}
            onStartUntil={handleStartUntil}
            onDone={handleVerify}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            showDoneButton={isAlarmSharePrefill}
            showCancelButton={isAlarmSharePrefill}
            hidePrimaryRowLabel={isAlarmSharePrefill}
            hideCategorySelector={isAlarmSharePrefill}
            hideRemoveButton={isAlarmSharePrefill}
            addActionButtonText={isAlarmSharePrefill ? 'Add SMS' : 'AND'}
            doneButtonText="Send Email"
            doneButtonVariant="primary"
          />
        </>
      )}

      {!isAlarmSharePrefill && (hasStartedUntil || builderState.until.length > 0) && (
        <>
          <div id="action-builder-until" className="overflow-hidden" style={{ height: 0 }} />
          <BuilderSection
            section="until"
            items={builderState.until}
            readOnly={editorReadOnly}
            rowErrors={validation.rowErrors}
            onUpdateItem={updateUntil}
            onRemoveItem={handleRemoveUntil}
            onAddRow={() => {
              if (builderState.until.length === 0) {
                addUntilGroup();
                return;
              }

              addUntil(createBuilderItem('until'), builderState.until.length - 1);
            }}
            onAddGroup={() => addUntilGroup()}
            onDone={handleVerify}
            isSubmitting={isSubmitting}
          />
        </>
      )}

      {(validation.formErrors.length > 0 || submitError) && (
        <Alert variant="warning" className="global-action-builder-validation mb-0 flex-shrink-0">
          {submitError ? (
            <>
              {submitError}
              <IpawsBackendFindingsList
                findings={submitFindings}
                inputLabelOverrides={GLOBAL_ACTION_FINDING_LABELS}
              />
            </>
          ) : (
            validation.formErrors.map((error, index) => {
              const target = getErrorTarget(error);
              // This splits the error message by the keywords TITLE, IF, THEN, UNTIL to bold them in the alert
              const formattedError = error
                .split(/\b(TITLE|IF|THEN|UNTIL)\b/)
                .map((part, i) =>
                  part === 'TITLE' || part === 'IF' || part === 'THEN' || part === 'UNTIL' ? (
                    <strong key={i}>{part}</strong>
                  ) : (
                    part
                  ),
                );
              return (
                <span key={error + '-' + index}>
                  {index > 0 && ' | '}
                  {target ? (
                    <a href={`#${target}`} className="text-reset">
                      {formattedError}
                    </a>
                  ) : (
                    formattedError
                  )}
                </span>
              );
            })
          )}
        </Alert>
      )}
    </div>
  );
};

export default ActionBuilder;
